import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, BookOpen, RefreshCw, CheckCircle2, AlertCircle } from 'lucide-react';
import { resetOpenLibraryEnrichmentStatus, getOpenLibraryEnrichmentStats, OpenLibraryResetStats } from '@/utils/resetOpenLibraryEnrichment';
import { Alert, AlertDescription } from '@/components/ui/alert';

export function OpenLibraryResetPanel() {
  const [stats, setStats] = useState<any>(null);
  const [resetResult, setResetResult] = useState<OpenLibraryResetStats | null>(null);
  const [isResetting, setIsResetting] = useState(false);
  const [isLoadingStats, setIsLoadingStats] = useState(true);
  const [hasReset, setHasReset] = useState(false);

  const loadStats = async () => {
    setIsLoadingStats(true);
    try {
      const data = await getOpenLibraryEnrichmentStats();
      setStats(data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    } finally {
      setIsLoadingStats(false);
    }
  };

  useEffect(() => {
    loadStats();
  }, []);

  const handleReset = async () => {
    if (!confirm('This will mark all OpenLibrary books with incomplete data as pending for re-enrichment. This affects thousands of books. Continue?')) {
      return;
    }

    setIsResetting(true);
    try {
      const result = await resetOpenLibraryEnrichmentStatus();
      setResetResult(result);
      setHasReset(true);
      // Reload stats after reset
      await loadStats();
    } catch (error) {
      console.error('Reset failed:', error);
    } finally {
      setIsResetting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-orange-500" />
              OpenLibrary Re-Enrichment (Phase 2)
            </CardTitle>
            <CardDescription>
              Mark OpenLibrary books with incomplete data for ISBNdb enrichment
            </CardDescription>
          </div>
          <Button
            onClick={handleReset}
            disabled={isResetting || isLoadingStats || hasReset}
            size="lg"
            variant={hasReset ? "outline" : "default"}
          >
            {isResetting ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Processing...
              </>
            ) : hasReset ? (
              <>
                <CheckCircle2 className="w-4 h-4 mr-2" />
                Reset Complete
              </>
            ) : (
              <>
                <AlertTriangle className="w-4 h-4 mr-2" />
                Reset Enrichment Status
              </>
            )}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Warning Alert */}
        {!hasReset && stats && stats.needsReEnrichment > 0 && (
          <Alert className="border-orange-500 bg-orange-50 dark:bg-orange-950">
            <AlertTriangle className="h-4 w-4 text-orange-600" />
            <AlertDescription>
              <strong className="text-orange-900 dark:text-orange-100">
                {stats.needsReEnrichment.toLocaleString()} OpenLibrary books
              </strong>
              <span className="text-orange-800 dark:text-orange-200">
                {' '}need re-enrichment. These books were imported from OpenLibrary but lack quality metadata like synopses, series information, or page counts.
              </span>
            </AlertDescription>
          </Alert>
        )}

        {/* Success Result */}
        {resetResult && (
          <Alert className="border-green-500 bg-green-50 dark:bg-green-950">
            <CheckCircle2 className="h-4 w-4 text-green-600" />
            <AlertDescription>
              <div className="space-y-2">
                <p className="font-semibold text-green-900 dark:text-green-100">
                  Successfully marked {resetResult.updated_count.toLocaleString()} books for re-enrichment!
                </p>
                <div className="text-sm text-green-800 dark:text-green-200 space-y-1">
                  <p>• {resetResult.books_with_no_synopsis.toLocaleString()} missing synopses</p>
                  <p>• {resetResult.books_with_no_series.toLocaleString()} missing series data</p>
                  <p>• {resetResult.books_with_no_pages.toLocaleString()} missing page counts</p>
                </div>
                <p className="text-sm text-green-800 dark:text-green-200 mt-2">
                  These books will now be processed by the automated enrichment system using ISBNdb.
                </p>
              </div>
            </AlertDescription>
          </Alert>
        )}

        {/* Stats Grid */}
        {isLoadingStats ? (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-24 bg-muted animate-pulse rounded-lg" />
            ))}
          </div>
        ) : stats ? (
          <>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <BookOpen className="w-8 h-8 text-muted-foreground" />
                    <div className="text-right">
                      <p className="text-2xl font-bold">{stats.totalComplete.toLocaleString()}</p>
                      <p className="text-xs text-muted-foreground">Total Complete</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <AlertTriangle className="w-8 h-8 text-orange-500" />
                    <div className="text-right">
                      <p className="text-2xl font-bold">{stats.needsReEnrichment.toLocaleString()}</p>
                      <p className="text-xs text-muted-foreground">Need Re-enrichment</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <RefreshCw className="w-8 h-8 text-blue-500" />
                    <div className="text-right">
                      <p className="text-2xl font-bold">{stats.alreadyPending.toLocaleString()}</p>
                      <p className="text-xs text-muted-foreground">Already Pending</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div className="text-3xl">📖</div>
                    <div className="text-right">
                      <p className="text-2xl font-bold">{stats.withISBN.toLocaleString()}</p>
                      <p className="text-xs text-muted-foreground">With ISBNs</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Info Box */}
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                <div className="space-y-2">
                  <p className="font-semibold">What happens when you click "Reset Enrichment Status"?</p>
                  <ol className="text-sm space-y-1 ml-4 list-decimal">
                    <li>All OpenLibrary books marked as "complete" but missing data will be changed to "pending"</li>
                    <li>These books will be queued for re-enrichment using ISBNdb (for books with ISBNs)</li>
                    <li>The automated enrichment system will process them in batches</li>
                    <li>Books will get English synopses, accurate page counts, and proper series data</li>
                  </ol>
                  <p className="text-sm text-muted-foreground mt-2">
                    <strong>Note:</strong> Books with ISBNs ({stats.canBenefitFromISBNdb.toLocaleString()}) will benefit most from ISBNdb enrichment.
                  </p>
                </div>
              </AlertDescription>
            </Alert>
          </>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            Failed to load statistics
          </div>
        )}

        {/* Data Quality Breakdown */}
        {stats && !isLoadingStats && (
          <div>
            <h3 className="font-semibold mb-3">Current Status</h3>
            <div className="space-y-2">
              <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                <span className="text-sm">Books marked as "complete"</span>
                <Badge variant="outline">{stats.totalComplete.toLocaleString()}</Badge>
              </div>
              <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                <span className="text-sm">Books with incomplete data</span>
                <Badge variant="outline" className="bg-orange-100 text-orange-800 border-orange-300">
                  {stats.needsReEnrichment.toLocaleString()}
                </Badge>
              </div>
              <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                <span className="text-sm">Books that can use ISBNdb</span>
                <Badge variant="outline" className="bg-blue-100 text-blue-800 border-blue-300">
                  {stats.canBenefitFromISBNdb.toLocaleString()}
                </Badge>
              </div>
              <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                <span className="text-sm">Already queued for enrichment</span>
                <Badge variant="outline">{stats.alreadyPending.toLocaleString()}</Badge>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
